import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, DialogContentText, Tooltip } from '@mui/material';

import { MiscellaneousItem, Trailer, TruckDriver } from '@paragon/contract-library';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { deleteTruckload, editTruckload } from '../../../actions/truckloadActions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { TruckloadProperties } from './TruckloadProperties';

export interface EditTruckloadDialogProps {
  readonly truckloadGuid: string;
}

export function EditTruckloadDialog(props: EditTruckloadDialogProps) {
  const { truckloadGuid } = props;
  const dispatch = useAppDispatch();
  const truckloads = useAppSelector((state) => state.truckloads);
  const trailers = useAppSelector((state) => state.trailers);
  const truckDrivers = useAppSelector((state) => state.truckDrivers);
  const truckload = truckloads[truckloadGuid];

  const [trailer, setTrailer] = useState<Trailer | null>(
    truckload.trailerGuid ? trailers[truckload.trailerGuid] : null,
  );
  const [truckDriver, setTruckDriver] = useState<TruckDriver | null>(
    truckload.truckDriverGuid ? truckDrivers[truckload.truckDriverGuid] : null,
  );
  const [shipDate, setShipDate] = useState<Date | null>(truckload.dateShipped ? new Date(truckload.dateShipped) : null);
  const [miscellaneousItems, setMiscellaneousItems] = useState<readonly MiscellaneousItem[]>(
    truckload.miscellaneousItems,
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [submitDisabledDueToPendingMiscellaneousItems, setSubmitDisabledDueToPendingMiscellaneousItems] =
    useState<boolean>(false);

  return (
    <Dialog
      title="Edit Truckload"
      open
      submitText="Edit"
      onSubmit={() => {
        dispatch(
          editTruckload(truckloadGuid, trailer?.guid ?? null, truckDriver?.guid ?? null, shipDate, miscellaneousItems),
        );
        dispatch(closeCurrentPopup());
      }}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '900px', height: '700px' }}
      submitDisabled={submitDisabledDueToPendingMiscellaneousItems}
      enterKeySubmits
      insistent
    >
      <Box sx={{ mb: 2 }}>Truckload {truckload.id}</Box>
      <Box sx={{ mb: 2 }}>
        <TruckloadProperties
          trailer={trailer}
          setTrailer={setTrailer}
          truckDriver={truckDriver}
          setTruckDriver={setTruckDriver}
          shipDate={shipDate}
          setShipDate={setShipDate}
          miscellaneousItems={miscellaneousItems}
          setMiscellaneousItems={setMiscellaneousItems}
          setParentSubmitDisabled={setSubmitDisabledDueToPendingMiscellaneousItems}
        />
      </Box>
      <Tooltip disableInteractive title="Delete truckload">
        <span>
          <Button
            variant="outlined"
            onClick={() => void setConfirmDeleteOpen(true)}
            startIcon={<DeleteIcon sx={{ color: 'red' }} />}
          >
            Delete
          </Button>
        </span>
      </Tooltip>
      <Dialog
        open={confirmDeleteOpen}
        variant="design"
        title="Confirm Delete"
        onSubmit={() => {
          dispatch(deleteTruckload(truckloadGuid));
          setConfirmDeleteOpen(false);
          dispatch(closeCurrentPopup());
        }}
        onCancel={() => void setConfirmDeleteOpen(false)}
      >
        <DialogContentText>
          Delete truckload {truckload.id}? All bundles associated with this truckload will no longer be associated with
          it.
        </DialogContentText>
      </Dialog>
    </Dialog>
  );
}
